import RestClient from './RestClient';
import User from '../../models/User';

class Users extends RestClient {
    constructor() {
        super({
            "model": User,
            "entryPoint": "kuser",
            "resource": "user",
            "resources": "users",
            "id": "id"
        });
    }

    lookupByEmail(email) {
        const encoded = encodeURIComponent(email);
        return this.GET(`/kuser/lookup_by_email/email/${encoded}`)
            .then(response => response.json())
            .then(json => {
                if (json.lookup !== undefined) {
                    return json;
                }
                throw new Error(json.flash?.error || 'Lookup failed');
            });
    }

    inactive() {
        return this.GET('/kuser/inactive')
            .then(response => response.json())
            .then(json => {
                if (json.users) {
                    json.users = json.users.map(u => new User(u));
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    hide(user_id) {
        let url = `/kuser/hide/id/${user_id}`;
        return this.POST(url)
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    applyAbilities(templateId, ids, includeSelf = false) {
        const url = `/kuser/apply_abilities/template_id/${templateId}`;
        return this.POST(url, { ids, include_self: includeSelf })
            .then(response => response.json())
            .then(json => {
                if (json.flash?.success) {
                    if (json.users) {
                        json.users = json.users.map(user => new User(user));
                    }
                    return json;
                }
                throw new Error(json.flash?.error);
            });
    }

    previewApplyAbilities(templateId, ids, includeSelf = false) {
        const url = `/kuser/preview_apply_abilities/template_id/${templateId}`;
        return this.POST(url, { ids, include_self: includeSelf })
            .then(response => response.json())
            .then(json => {
                if (json.preview) {
                    return json.preview;
                }
                throw new Error(json.flash?.error);
            });
    }

    addSector(params) {
        let url = `/kuser/add_sector/id/${params.user_id}`;
         return this.PUT( url, { 'user_id': params.user_id, 'sector_id': params.sector_id }
            ).then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    removeSector(params) {
        let url = `/kuser/remove_sector/id/${params.user_id}`;
        return this.PUT( url, { 'user_id': params.user_id, 'sector_id': params.sector_id }
            ).then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json;
                }
                throw new Error(json.flash.error);
            });
    }

    revealApiKey(user_id) {
        return this.GET(`/kuser/reveal_api_key/id/${user_id}`)
            .then(response => response.json())
            .then(json => {
                if (json.api_key) {
                    return json.api_key;
                }
                const err = json.flash?.error;
                throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : 'Reveal failed');
            });
    }

    sendLoginInvitation(user_id) {
        return this.POST(`/kuser/send_login_invitation/id/${user_id}`)
            .then(response => response.json())
            .then(json => {
                if (json.flash?.success) {
                    const msg = json.flash.success;
                    return Array.isArray(msg) ? msg.join(', ') : msg;
                }
                const err = json.flash?.error;
                throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : 'Send failed');
            });
    }

    syncRewards(user_id) {
        return this.POST(`/kuser/sync_rewards/id/${user_id}`)
            .then(response => response.json())
            .then(json => {
                if (json.flash?.success) {
                    const msg = json.flash.success;
                    return Array.isArray(msg) ? msg.join(', ') : msg;
                }
                const err = json.flash?.error;
                throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : 'Sync failed');
            });
    }

    cancelPendingEmail(user_id) {
        return this.POST(`/kuser/cancel_pending_email/id/${user_id}`)
            .then(response => response.json())
            .then(json => {
                if (json.user) {
                    json.user = new User(json.user);
                    return json;
                }
                const err = json.flash?.error;
                throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : 'Cancel failed');
            });
    }

    /**
     * Opaque JSON config for the current user's membership on an instance.
     * GET /kuser/instance_config/tkt_instance_id/:uuid → { config }
     */
    getInstanceConfig(tktInstanceId) {
        const id = encodeURIComponent(tktInstanceId);
        return this.GET(`/kuser/instance_config/tkt_instance_id/${id}`)
            .then(response => this.parseResponseJson(response))
            .then(json => {
                if (json && typeof json.config === 'object' && json.config !== null) {
                    return json.config;
                }
                const err = json?.flash?.error;
                throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : 'Load config failed');
            });
    }

    /**
     * Deep-merge a patch into the current user's instance config.
     * PUT /kuser/instance_config/tkt_instance_id/:uuid body { config } → { config }
     * List arrays under a key are replaced wholesale by the server.
     */
    mergeInstanceConfig(tktInstanceId, patch) {
        const id = encodeURIComponent(tktInstanceId);
        return this.PUT(`/kuser/instance_config/tkt_instance_id/${id}`, { config: patch })
            .then(response => this.parseResponseJson(response))
            .then(json => {
                if (json && typeof json.config === 'object' && json.config !== null) {
                    return json.config;
                }
                const err = json?.flash?.error;
                throw new Error(err ? (Array.isArray(err) ? err.join(', ') : err) : 'Save config failed');
            });
    }
}

const UsersApi = new Users();

export default UsersApi;
